"use client";

import { motion } from "framer-motion";
import { useTranslations } from "next-intl";
import { IoSettingsOutline, IoChevronForwardOutline } from "react-icons/io5";
import NetworkHub from "./heroImg";
import { HubNode } from "@/types/types";
import { useRef, useState } from "react";
import { defaultNodes } from "@/data/main-data";

export default function Hero() {
  const t = useTranslations(`Hero`);

  const [nodes] = useState<HubNode[]>(defaultNodes);
  const [isGenerating] = useState(false);
  const svgRef = useRef<SVGSVGElement>(null);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) element.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center justify-center overflow-hidden bg-[#000000] pt-20 px-5"
    >
      {/* 1. Background Layers */}
      <div className="absolute inset-0 z-0">
        {/* Modern Dot Grid Background */}
        <div
          className="absolute inset-0 opacity-[0.2]"
          style={{
            backgroundImage: `radial-gradient(#00d4ff 0.5px, transparent 0.5px)`,
            backgroundSize: "30px 30px",
          }}
        ></div>

        {/* Massive Dynamic Blurs */}
        <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] rounded-full bg-main-color/50 blur-[140px]" />
        <div className="absolute bottom-[10%] right-[-5%] w-[40%] h-[40%] rounded-full bg-[#00d4ff]/10 blur-[120px] animate-pulse" />
      </div>

      {/* 2. Content */}
      <div className="relative z-10 container mx-auto px-4 md:px-0 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
        {/* Left Content */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center lg:text-left"
        >
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="inline-flex items-center gap-3 px-4 py-2 mb-6 rounded-full border border-[#00d4ff]/20 bg-[#00d4ff]/5 backdrop-blur-xl"
          >
            <span className="flex h-2 w-2 rounded-full bg-[#00d4ff] animate-pulse shadow-[0_0_10px_#00d4ff]"></span>
            <span className="text-[#00d4ff] text-[11px] font-bold tracking-[0.2em] uppercase">
              {t("gen")}
            </span>
          </motion.div>

          {/* Headline */}
          <h1 className="text-5xl md:text-7xl font-black text-white leading-[1.05] mb-8">
            {t("scale")} <br />
            <span className="text-transparent bg-clip-text bg-linear-to-r from-[#00d4ff] via-[#ffffff] to-[#00d4ff] bg-size-[200%_auto] animate-gradient-x">
              {t("smart")}
            </span>
          </h1>

          <p className="text-gray-400 text-lg md:text-xl mb-10 max-w-xl mx-auto lg:mx-0 font-light leading-relaxed">
            {t("start")}
          </p>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-5 justify-center lg:justify-start">
            <motion.button
              whileHover={{
                scale: 1.05,
                boxShadow: "0 0 40px rgba(0, 212, 255, 0.3)",
              }}
              whileTap={{ scale: 0.98 }}
              className="px-10 py-4 bg-white text-black rounded-full font-bold text-lg transition-all flex items-center justify-center gap-2 group cursor-pointer"
              onClick={() => scrollToSection("services")}
            >
              {t("getStarted")}
              <IoChevronForwardOutline className="group-hover:translate-x-1 transition-transform" />
            </motion.button>

            <motion.button
              whileHover={{ border: "1px solid #00d4ff", color: "#00d4ff" }}
              className="px-10 py-4 border border-white/20 text-white rounded-full font-bold text-lg backdrop-blur-sm transition-all cursor-pointer"
              onClick={() => scrollToSection("contact")}
            >
              {t("contactUs")}
            </motion.button>
          </div>
        </motion.div>

        {/* Right Content - Visual */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1 }}
          className="relative"
        >
          <NetworkHub
            nodes={nodes}
            isGenerating={isGenerating}
            svgRef={svgRef}
          />

          {/* Aura Glow */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-[#00d4ff]/5 blur-[120px] -z-10 animate-pulse"></div>
        </motion.div>
      </div>

      {/* Decorative Rotating Tech Circle */}
      <div className="absolute -bottom-20 -left-20 opacity-20 pointer-events-none">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
          className="relative w-96 h-96 border-px border-[#00d4ff]/30 rounded-full flex items-center justify-center"
        >
          <div className="w-[80%] h-[80%] border-px border-[#00d4ff]/10 rounded-full border-dashed"></div>
          <IoSettingsOutline className="absolute text-[#00d4ff] text-4xl top-0 left-1/2 -translate-x-1/2" />
        </motion.div>
      </div>
    </section>
  );
}
